import type { Locale } from '@lims/shared-types';

/**
 * Formatting for the PUBLIC verification page.
 *
 * Kept apart from `components/format.ts` because the public page has one
 * constraint no other screen has: it knows nothing about the laboratory beyond
 * the allow-list (D122) — in particular no time zone.
 *
 * Western digits in both languages and the Gregorian calendar, for the same
 * reasons `components/format.ts` gives: a value on this page is read beside the
 * same value printed on the report.
 */

function intlLocale(locale: Locale): string {
  return locale === 'ar' ? 'ar-SA-u-ca-gregory-nu-latn' : 'en-GB';
}

/**
 * The issue moment, in the READER's time zone, and NAMING that zone.
 *
 * The allow-list carries no laboratory time zone, so the page cannot show the
 * moment as the laboratory's clock read it. A bare date in the reader's zone
 * could name a different day from the printed report with no hint why; naming
 * the zone makes the moment unambiguous wherever the page is opened.
 */
export function formatIssuedAt(iso: string, locale: Locale): string | null {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;
  return new Intl.DateTimeFormat(intlLocale(locale), {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    timeZoneName: 'short',
  }).format(date);
}

/**
 * A wait, in words. The unit's name AND its plural form come from `Intl`, not
 * from the catalogues: Arabic has six plural forms and English two, and a key
 * per form would break the guarantee that both catalogues hold the same keys.
 */
export function formatWait(seconds: number, locale: Locale): string {
  const whole = Math.max(1, Math.ceil(seconds));
  const inMinutes = whole >= 120;
  return new Intl.NumberFormat(intlLocale(locale), {
    style: 'unit',
    unit: inMinutes ? 'minute' : 'second',
    unitDisplay: 'long',
  }).format(inMinutes ? Math.ceil(whole / 60) : whole);
}

export interface LanguageRun {
  readonly text: string;
  /** The language the text was recorded in, for the element's `lang`. */
  readonly lang: Locale;
}

/**
 * The interface language first, the other language as a fallback — and which
 * one it was, so the run can declare its own `lang`. Nothing is translated: a
 * name recorded only in English is shown in English on the Arabic page.
 */
export function pickWithLanguage(
  value: { readonly ar: string | null; readonly en: string | null },
  locale: Locale,
): LanguageRun | null {
  const other: Locale = locale === 'ar' ? 'en' : 'ar';
  const preferred = value[locale];
  if (preferred) return { text: preferred, lang: locale };
  const fallback = value[other];
  return fallback ? { text: fallback, lang: other } : null;
}

/**
 * The name a downloaded report is saved under: its number and, when known, the
 * version actually received. Never a storage key or a file id.
 */
export function verificationFileName(
  reportNumber: string,
  versionNumber: number | null,
): string {
  const safe = reportNumber.replace(/[\\/:*?"<>|\s]+/g, '-');
  return versionNumber === null ? `${safe}.pdf` : `${safe}-v${versionNumber}.pdf`;
}
